import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import ConfirmSubmitModal from './ConfirmSubmitModal';

const EditDefaultPaymentPopup = ({ selectedTransaction, grantList, onSave, onCancel }) => {
  const [defaultPayment, setDefaultPayment] = useState(
    typeof selectedTransaction.defaultPayment === 'string'
      ? JSON.parse(selectedTransaction.defaultPayment || '{}')
      : selectedTransaction.defaultPayment || {}
  );

  const handlePercentageChange = (grant, percentage) => {
    setDefaultPayment(prev => ({
      ...prev,
      [grant]: percentage
    }));
  };

  const handleSubmit = () => {
    const totalPercentage = Object.values(defaultPayment)
      .reduce((sum, value) => sum + parseFloat(value || 0), 0);

    if (Math.abs(totalPercentage - 100) > 0.01) {
      alert(`Total percentage must equal 100%. Current total: ${totalPercentage.toFixed(2)}%`);
      return;
    }
    onSave({ ...selectedTransaction, defaultPayment });
  };

  return (
    <div className="card p-6 max-w-md mx-auto animate-fade-in">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Payment</h3>
      <div className="space-y-4">
        {grantList.map((grant) => (
          <div key={grant} className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">{grant}</label>
            <input
              type="number"
              value={defaultPayment[grant] || ''}
              onChange={(e) => handlePercentageChange(grant, e.target.value)}
              min="0"
              max="100"
              step="0.01"
              className="input-field"
              placeholder="0.00"
            />
          </div>
        ))}
      </div>
      <div className="flex gap-3 mt-6">
        <button onClick={handleSubmit} className="btn-primary flex-1">Save</button>
        <button onClick={onCancel} className="btn-secondary flex-1">Cancel</button>
      </div>
    </div>
  );
};

const CreditCardDepositTable = ({ depositTransactions, initialFilterStartDate, initialFilterEndDate }) => {
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [showConfirmedRecords, setShowConfirmedRecords] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [customizedCategories, setCustomizedCategories] = useState([]);
  const [showEditDefaultPaymentPopup, setShowEditDefaultPaymentPopup] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [grantList, setGrantList] = useState([]);
  const [depositTypes, setDepositTypes] = useState([]);
  const [categoriesByDepositType, setCategoriesByDepositType] = useState({});
  const fixedDepositTypes = ['Internal Bank Transfer', 'N/A (Non-grant Item)'];
  const depositCategories = [...depositTypes, ...fixedDepositTypes];

  const [showEditNotePopup, setShowEditNotePopup] = useState(false);
  const [transactionToEditNote, setTransactionToEditNote] = useState(null);

  // Bulk select-all + bulk-set deposit type for unconfirmed CC deposit transactions
  const [showBulkCategoryPopup, setShowBulkCategoryPopup] = useState(false);
  const [bulkCategoryValue, setBulkCategoryValue] = useState("");

  const handleSelectAllVisible = async (shouldCheck) => {
    const visibleIds = filteredTransactions
      .filter((t) => t.Confirmed !== "Yes" && t.ID)
      .map((t) => t.ID);
    if (visibleIds.length === 0) return;
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/credit-card/deposit/bulk-update`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: visibleIds, Checked: shouldCheck }),
      });
      if (!resp.ok) throw new Error('Bulk update failed');
      setFilteredTransactions((prev) => prev.map((t) =>
        visibleIds.includes(t.ID) ? { ...t, Checked: shouldCheck ? "Yes" : "No" } : t
      ));
    } catch (error) {
      console.error("Error in CC deposit select-all:", error);
      alert("Error updating check status for visible records.");
    }
  };

  const handleBulkSetCategory = async () => {
    if (!bulkCategoryValue) {
      alert("Please choose a deposit type to apply.");
      return;
    }
    const targets = filteredTransactions.filter((t) => t.ID && t.Confirmed !== "Yes");
    if (targets.length === 0) {
      alert("No unconfirmed transactions to update.");
      return;
    }
    try {
      const ids = targets.map(t => t.ID);
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/credit-card/deposit/bulk-update`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, grantCategory: bulkCategoryValue }),
      });
      if (!resp.ok) throw new Error('Bulk update failed');
      setFilteredTransactions(prev => prev.map(t =>
        ids.includes(t.ID) ? { ...t, "Grant Category": bulkCategoryValue } : t
      ));
      setShowBulkCategoryPopup(false);
      setBulkCategoryValue("");
    } catch (e) {
      console.error('CC deposit bulk set failed:', e);
      alert('Failed to bulk set deposit type.');
    }
  };

  useEffect(() => {
    if (initialFilterStartDate && initialFilterEndDate) {
      setFilterStartDate(initialFilterStartDate);
      setFilterEndDate(initialFilterEndDate);
      setFilterPeriod('custom');
    }
  }, [initialFilterStartDate, initialFilterEndDate]);

  const handleEditNoteClick = (transaction) => {
    setTransactionToEditNote(transaction);
    setShowEditNotePopup(true);
  };

  // Add this handler for saving the note
  const handleSaveNote = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/credit-card/deposit/note/${transactionToEditNote.ID}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ Note: transactionToEditNote.Note }),
        }
      );

      if (response.ok) {
        // Update the local state to reflect the changes
        const updatedTransactions = filteredTransactions.map(t =>
          t.ID === transactionToEditNote.ID
            ? { ...t, Note: transactionToEditNote.Note }
            : t
        );
        setFilteredTransactions(updatedTransactions);
        setShowEditNotePopup(false);
      } else {
        throw new Error('Failed to update note');
      }
    } catch (error) {
      console.error('Error updating note:', error);
      alert('Error updating note.');
    }
  };

  const fetchGrantList = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grants/grant-list`, {
        method: 'GET',
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const grantData = await response.json();
      setGrantList(grantData);
    } catch (error) {
      console.error('Error fetching grant list:', error);
    }
  }, []);

  const fetchCustomizedCategories = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/cc-deposit-categories`, {
        method: 'GET',
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const categories = await response.json();
      setCustomizedCategories(categories);
    } catch (error) {
      console.error('Error fetching customized categories:', error);
    }
  }, []);

  const fetchDepositTypes = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/cc-deposit-categories/deposit-types`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      setDepositTypes(data);
    } catch (error) {
      console.error('Error fetching deposit types:', error);
    }
  }, []);

  const fetchCategoriesByDepositType = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/cc-deposit-categories/by-deposit-type`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      setCategoriesByDepositType(data);
    } catch (error) {
      console.error('Error fetching categories by deposit type:', error);
    }
  }, []);

  useEffect(() => {
    fetchCustomizedCategories();
    fetchGrantList();
    fetchDepositTypes();
    fetchCategoriesByDepositType();
  }, [fetchCustomizedCategories, fetchGrantList, fetchDepositTypes, fetchCategoriesByDepositType]);

  useEffect(() => {
    filterTransactions();
  }, [depositTransactions, showConfirmedRecords, filterPeriod, filterStartDate, filterEndDate]);

  const filterTransactions = () => {
    let filtered = depositTransactions;
    filtered = filtered.filter(t => t.Confirmed !== 'Yes');

    if (filterPeriod !== 'all') {
      const currentDate = new Date();
      let startDate, endDate;

      switch (filterPeriod) {
        case "lastMonth":
          startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
          endDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
          endDate.setHours(23, 59, 59, 999);
          break;
        case "lastQuarter":
          startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 3, 1);
          endDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
          endDate.setHours(23, 59, 59, 999);
          break;
        case "custom":
          startDate = new Date(filterStartDate);
          endDate = new Date(filterEndDate);
          endDate.setHours(23, 59, 59, 999);
          break;
        default:
          if (!isNaN(parseInt(filterPeriod))) {
            // Year filtering
            startDate = new Date(parseInt(filterPeriod), 0, 1);
            endDate = new Date(parseInt(filterPeriod), 11, 31);
            endDate.setHours(23, 59, 59, 999);
          }
      }

      if (startDate && endDate) {
        filtered = filtered.filter((t) => {
          const transactionDate = new Date(t["Date (UTC)"]);
          return transactionDate >= startDate && transactionDate <= endDate;
        });
      }
    }

    setFilteredTransactions(filtered);
  };

  const getFilterPeriodOptions = () => {
    const years = [...new Set(depositTransactions.map(t =>
      new Date(t["Date (UTC)"]).getFullYear()
    ))];

    return [
      { value: "all", label: "All" },
      ...years.map(year => ({ value: year.toString(), label: year.toString() })),
      { value: "lastMonth", label: "Last Month" },
      { value: "lastQuarter", label: "Last Quarter" },
      { value: "custom", label: "Custom Period" },
    ];
  };

  const handleFilterPeriodChange = (e) => {
    const period = e.target.value;
    setFilterPeriod(period);

    // Show popup for custom period
    if (period === 'custom') {
      setShowFilterPopup(true);
    }
  };

  const handleCheckboxChange = async (id, checked) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/credit-card/deposit/check/${id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ Checked: checked }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const updatedTransactions = filteredTransactions.map(t =>
        t.ID === id ? { ...t, Checked: checked ? 'Yes' : 'No' } : t
      );
      setFilteredTransactions(updatedTransactions);
    } catch (error) {
      console.error('Error updating check status:', error);
      alert('Error updating check status.');
    }
  };

  const handleConfirmCheckedRecords = async () => {
    setShowConfirmModal(false);

    const checkedIds = filteredTransactions
      .filter(transaction => transaction.Checked === 'Yes')
      .map(transaction => transaction.ID);

    if (checkedIds.length === 0) {
      alert('No records selected to confirm.');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/credit-card/deposit/confirmChecked`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ checkedIds }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      filterTransactions();
      alert('Checked records confirmed successfully');
    } catch (error) {
      console.error('Error confirming checked records:', error);
      alert('Error confirming checked records.');
    }
  };

  const handleAutoFill = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/credit-card/deposit/autofill`, {
        method: 'POST',
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      filterTransactions();
      alert('Auto-fill completed successfully');
    } catch (error) {
      console.error('Error auto-filling data:', error);
      alert('Error auto-filling data.');
    }
  };

  const handleCategoryChange = async (transaction, category, categoryType) => {
    try {
      const updatedTransaction = {
        ...transaction,
        [categoryType]: category,
      };

      console.log('Updating transaction:', updatedTransaction);

      if (categoryType === 'Grant Category' &&
          category !== 'Refund (Grant Item)' &&
          transaction['Grant Category'] === 'Refund (Grant Item)') {
        updatedTransaction['Default Payment'] = '{}';
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/credit-card/deposit/${transaction.ID}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedTransaction),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const updatedTransactions = filteredTransactions.map(t =>
        t.ID === transaction.ID ? updatedTransaction : t
      );
      setFilteredTransactions(updatedTransactions);
    } catch (error) {
      console.error('Error updating category:', error);
      alert('Error updating category.');
    }
  };

  const handleEditDefaultPaymentClick = (transaction) => {

    if (transaction['Grant Category'] !== 'Refund (Grant Item)') {
      alert('Default Payment can only be edited for "Refund (Grant Item)" deposits.');
      return;
    }

    let defaultPayment = transaction['Default Payment'];
    if (typeof defaultPayment === 'string') {
      try {
        defaultPayment = JSON.parse(defaultPayment);
      } catch (error) {
        defaultPayment = {};
      }
    }
    setSelectedTransaction({
      ...transaction,
      defaultPayment: defaultPayment
    });
    setShowEditDefaultPaymentPopup(true);
  };

  const handleEditDefaultPaymentSubmit = async (selectedTransaction) => {
    if (!selectedTransaction) {
      alert('No transaction selected');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/credit-card/deposit/defaultPayment/${selectedTransaction.ID}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          defaultPayment: selectedTransaction.defaultPayment,
        }),
      });

      if (response.ok) {
        const updatedTransactions = filteredTransactions.map(t =>
          t.ID === selectedTransaction.ID
            ? { ...t, 'Default Payment': JSON.stringify(selectedTransaction.defaultPayment) }
            : t
        );
        setFilteredTransactions(updatedTransactions);
        setShowEditDefaultPaymentPopup(false);
      } else {
        const errorText = await response.text();
        alert(`Failed to update default payment: ${errorText}`);
      }
    } catch (error) {
      console.error('Error updating default payment:', error);
      alert(`Error updating default payment: ${error.message}`);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-xl font-semibold text-gray-900">Credit Card Deposit Transactions</h2>
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Period:</label>
              <select value={filterPeriod} onChange={handleFilterPeriodChange} className="input-field min-w-[120px]">
                {getFilterPeriodOptions().map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <button onClick={() => setShowConfirmModal(true)} className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors">
              Submit Approved Records
            </button>
            <button onClick={handleAutoFill} className="btn-secondary">
              AutoFill
            </button>
            <Link to="/customized-deposit-categories">
              <button className="btn-secondary">
                Edit Deposit Type/Subcategory
              </button>
            </Link>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden relative z-0">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date (UTC)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bank Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex flex-col items-start gap-1">
                    <span>Deposit Type</span>
                    <button
                      onClick={() => setShowBulkCategoryPopup(true)}
                      className="text-[10px] px-2 py-0.5 bg-blue-600 text-white rounded hover:bg-blue-700 normal-case font-medium"
                      title="Set deposit type for all unconfirmed transactions"
                    >
                      Bulk Set
                    </button>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subcategory</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment (%)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 z-20 bg-gray-50" style={{ boxShadow: '-2px 0 4px rgba(0,0,0,0.05)' }}>
                  <div className="flex flex-col items-start gap-1">
                    <span>Approve</span>
                    <button
                      onClick={() => {
                        const unconfirmed = filteredTransactions.filter(t => t.Confirmed !== "Yes");
                        const allChecked = unconfirmed.length > 0 && unconfirmed.every(t => t.Checked === "Yes");
                        handleSelectAllVisible(!allChecked);
                      }}
                      className="text-[10px] px-2 py-0.5 bg-blue-600 text-white rounded hover:bg-blue-700 normal-case font-medium"
                      title="Toggle select all unconfirmed transactions"
                    >
                      Select All
                    </button>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTransactions.map((transaction) => (
                <tr
                  key={transaction.ID}
                  className={`hover:bg-gray-50 transition-colors duration-150 ${transaction.Checked === 'Yes' ? 'bg-primary-50 border-l-4 border-primary-400' : ''}`}
                >
                  <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-900">
                    {new Date(transaction['Date (UTC)']).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-5 whitespace-nowrap text-sm font-medium text-green-600">
                    ${parseFloat(transaction.Amount).toFixed(2)}
                  </td>
                  <td className="px-6 py-5 text-sm text-gray-900 max-w-xs truncate">
                    {transaction['Bank Description']}
                  </td>
                  <td className="px-6 py-5 whitespace-nowrap">
                    <select
                      value={transaction['Grant Category'] || ''}
                      onChange={(e) => handleCategoryChange(transaction, e.target.value, 'Grant Category')}
                      className="input-field text-sm min-w-[140px]"
                    >
                      <option value="">Select Category</option>
                      {depositCategories.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-6 py-5 whitespace-nowrap">
                    <select
                      value={transaction['Customized Category'] || 'N/A'}
                      onChange={(e) => handleCategoryChange(transaction, e.target.value, 'Customized Category')}
                      className="input-field text-sm min-w-[120px]"
                    >
                      <option value="N/A">N/A</option>
                      {(() => {
                        const selectedDepositType = transaction['Grant Category'] || '';
                        const typeSpecific = categoriesByDepositType[selectedDepositType] || [];
                        const unassigned = categoriesByDepositType['Unassigned'] || categoriesByDepositType[''] || [];
                        const merged = [...new Set([...typeSpecific, ...unassigned])].filter(c => c !== 'N/A');
                        return merged.map((category) => (
                          <option key={category} value={category}>{category}</option>
                        ));
                      })()}
                    </select>
                  </td>
                  <td className="px-6 py-5 text-sm text-gray-900">
                    {transaction['Grant Category'] === 'Refund (Grant Item)' ? (
                      <div className="max-w-xs">
                        {Object.entries(JSON.parse(transaction['Default Payment'] || '{}')).map(([source, percentage]) =>
                          `${source}: ${percentage}%`
                        ).join(', ')}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">N/A</span>
                    )}
                  </td>
                  <td className="px-6 py-5 whitespace-nowrap">
                    <div className="flex gap-2">
                      {transaction['Grant Category'] === 'Refund (Grant Item)' ? (
                        <button
                          onClick={() => handleEditDefaultPaymentClick(transaction)}
                          className="px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100 transition-colors duration-200"
                        >
                          Edit Payment
                        </button>
                      ) : (
                        <button
                          disabled
                          className="px-3 py-1.5 text-xs font-medium text-gray-400 bg-gray-50 border border-gray-200 rounded-md cursor-not-allowed"
                        >
                          Edit Payment
                        </button>
                      )}
                      <button
                        onClick={() => handleEditNoteClick(transaction)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors duration-200 ${
                          transaction.Note
                            ? 'text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100'
                            : 'text-gray-600 bg-gray-50 border border-gray-200 hover:bg-gray-100'
                        }`}
                      >
                        {transaction.Note ? 'Edit Note' : 'Add Note'}
                      </button>
                    </div>
                  </td>
                  <td className={`px-6 py-5 whitespace-nowrap sticky right-0 z-10 ${transaction.Checked === 'Yes' ? 'bg-primary-50' : 'bg-white'}`} style={{ boxShadow: '-2px 0 4px rgba(0,0,0,0.05)' }}>
                    <input
                      type="checkbox"
                      checked={transaction.Checked === 'Yes'}
                      onChange={(e) => handleCheckboxChange(transaction.ID, e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded transition-colors duration-200"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showEditNotePopup && transactionToEditNote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="card p-6 max-w-lg w-full animate-slide-up">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Transaction Note</h3>
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <p className="text-sm">
                  <span className="font-medium text-gray-700">Date:</span>{" "}
                  <span className="text-gray-900">
                    {new Date(transactionToEditNote['Date (UTC)']).toLocaleDateString()}
                  </span>
                </p>
                <p className="text-sm">
                  <span className="font-medium text-gray-700">Description:</span>{" "}
                  <span className="text-gray-900">
                    {transactionToEditNote['Bank Description']}
                  </span>
                </p>
                <p className="text-sm">
                  <span className="font-medium text-gray-700">Amount:</span>{" "}
                  <span className="text-green-600 font-medium">
                    ${parseFloat(transactionToEditNote.Amount).toFixed(2)}
                  </span>
                </p>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Note:</label>
                <textarea
                  value={transactionToEditNote.Note || ''}
                  onChange={(e) => setTransactionToEditNote({
                    ...transactionToEditNote,
                    Note: e.target.value
                  })}
                  placeholder="Add your notes here..."
                  rows={4}
                  className="input-field resize-none"
                />
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={handleSaveNote} className="btn-primary flex-1">
                  Save Note
                </button>
                <button
                  onClick={() => setShowEditNotePopup(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditDefaultPaymentPopup && selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fade-in">
          <EditDefaultPaymentPopup
            selectedTransaction={selectedTransaction}
            grantList={grantList}
            onSave={handleEditDefaultPaymentSubmit}
            onCancel={() => setShowEditDefaultPaymentPopup(false)}
          />
        </div>
      )}

      {showFilterPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="card p-6 max-w-md mx-auto animate-slide-up">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Custom Period</h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Start Date</label>
                <input
                  type="date"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                  className="input-field"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">End Date</label>
                <input
                  type="date"
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                  className="input-field"
                />
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setFilterPeriod('custom');
                    setShowFilterPopup(false);
                  }}
                  className="btn-primary flex-1"
                >
                  Apply
                </button>
                <button
                  onClick={() => setShowFilterPopup(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmSubmitModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmCheckedRecords}
      />

      {showBulkCategoryPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Bulk Set Deposit Type</h3>
            <p className="text-sm text-gray-600 mb-3">
              Apply this deposit type to all unconfirmed transactions visible in the current view:
            </p>
            <select
              className="w-full border border-gray-300 rounded px-2 py-1 mb-4"
              value={bulkCategoryValue}
              onChange={(e) => setBulkCategoryValue(e.target.value)}
            >
              <option value="">Select deposit type</option>
              {depositCategories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                onClick={() => { setShowBulkCategoryPopup(false); setBulkCategoryValue(""); }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                onClick={handleBulkSetCategory}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditCardDepositTable;
